/**
 * Collapse non-trading gaps in a sorted array of candle bars.
 * For intraday data, detects session breaks (gaps > 3× normal interval)
 * and re-indexes timestamps so bars are evenly spaced at exactly one
 * interval. The original timestamp is preserved as `time_real` so cursors
 * and tooltips can still reference the real wall-clock time.
 *
 * @param {Array} bars  Sorted array of {time, open, high, low, close, ...}
 * @param {string} tf   Timeframe code like 'M15', 'H1', 'D1'
 * @returns {Array}     New array with compressed timestamps
 */
export const TF_SECONDS = { S1:1, M1:60, M5:300, M15:900, M30:1800, H1:3600, H4:14400, D1:86400, W1:604800 };

export function sortAndDeduplicateBars(bars) {
    if (!Array.isArray(bars)) return [];
    const byTime = new Map();
    bars.forEach((bar) => {
        if (!bar || typeof bar !== 'object') return;
        const time = Number(bar.time);
        if (!Number.isFinite(time)) return;
        byTime.set(time, { ...bar, time });
    });
    return Array.from(byTime.values()).sort((a, b) => a.time - b.time);
}

export function compressGaps(bars, tf) {
    const source = sortAndDeduplicateBars(bars);
    if (source.length < 2) return source;
    const upper = (tf || '').toUpperCase();
    // Only compress intraday timeframes (sub-daily)
    if (['D1', 'W1', 'MN', 'MN1'].includes(upper)) return source;
    const interval = TF_SECONDS[upper] || 300;
    const gapThreshold = interval * 3;
    const result = [];
    let cumOffset = 0;
    for (let i = 0; i < source.length; i++) {
        const t = source[i].time;
        if (i > 0) {
            const prevT = source[i - 1].time;
            const gap = t - prevT;
            if (gap > gapThreshold) cumOffset += gap - interval;
        }
        result.push({ ...source[i], time: t - cumOffset, time_real: t });
    }
    return result;
}
